import { useState } from 'react';
import { groupJoinCounts } from '@heraion/shared';

import type { GroupOption } from '../ExercisePicker/ExercisePicker';
import type { DraftEntry, LoggerDispatch } from './state/loggerDraft';
import type { WeightUnit } from './state/loggerDraftTypes';

/** The pending alignment question: the join has already happened, only the
 *  set counts are still undecided. */
export type GroupAlignPrompt = {
  groupId: string;
  shortest: number;
  longest: number;
};

// Join the current entry to an existing superset, prompting to align set
// counts when the target group's members have a different number of sets
// (the interleaved 1A·1B·2A·2B render goes "dirty" otherwise — same logic
// the old GroupPickerModal carried).
//
// RW-25 — the question used to be an `Alert.alert` with THREE buttons, which
// inside packages/ui never fired on react-native-web: joining a group with
// mismatched counts silently did nothing there. A three-way answer cannot
// travel through the ConfirmFn seam (title/body/yes/no), so it became a shared
// RN <GroupAlignDialog>, identical on both platforms.
//
// ⛔ That turns this hook from "returns a callback" into "returns a callback
// PLUS the pending question", and the state has to live here rather than
// inside the picker: ExercisePicker calls `onClose()` synchronously right
// after `onJoinGroup` (ExercisePicker.tsx), so a dialog rendered inside the
// picker's own tree would unmount the instant it was raised. The Alert got
// away with it only because it was an OS overlay outside the React tree.
export function useJoinExistingGroup({
  supersetIdx,
  entries,
  dispatch,
  defaultUnit,
}: {
  supersetIdx: number | null;
  entries: DraftEntry[];
  dispatch: LoggerDispatch;
  defaultUnit: WeightUnit;
}): {
  /** Picker callback — joins, and raises the alignment question if needed. */
  onPick: (option: GroupOption) => void;
  /** Non-null while the question is on screen. */
  prompt: GroupAlignPrompt | null;
  /** Bring every member up to the longest count. */
  pad: () => void;
  /** Cut every member down to the shortest count. */
  trim: () => void;
  /** Leave the counts uneven — also the answer for back/backdrop. */
  keep: () => void;
} {
  const [prompt, setPrompt] = useState<GroupAlignPrompt | null>(null);

  const align = (mode: 'pad' | 'trim') => {
    if (!prompt) return;
    dispatch({
      type: 'align_group_sets',
      groupId: prompt.groupId,
      mode,
      defaultUnit,
    });
    setPrompt(null);
  };

  const onPick = (option: GroupOption) => {
    if (supersetIdx === null) return;
    const joiningIdx = supersetIdx;
    const joiningEntry = entries[joiningIdx];
    const group = { id: option.id, kind: option.kind };
    const doJoin = () =>
      dispatch({ type: 'set_group', entryIdxs: [joiningIdx], group });
    if (!joiningEntry) {
      doJoin();
      return;
    }
    const otherCounts: number[] = [];
    for (let i = 0; i < entries.length; i++) {
      if (i === joiningIdx) continue;
      const e = entries[i];
      if (e?.group?.id === group.id) otherCounts.push(e.sets.length);
    }
    const counts = groupJoinCounts(joiningEntry.sets.length, otherCounts);
    if (!counts) {
      doJoin();
      return;
    }
    // ⭐ The join happens NOW, not inside each answer. In the Alert version
    // every button carried its own `doJoin()`, so walking away (Android back)
    // joined nothing: the athlete had picked a group and got silence. With a
    // real dialog the dismiss is a normal, easy gesture, so the join — the
    // thing actually asked for — is committed up front and the three answers
    // decide only the ALIGNMENT. Nothing is destroyed by this: `pad`/`trim`
    // are the only legs that touch set counts, and neither runs on dismiss.
    doJoin();
    setPrompt({ groupId: group.id, ...counts });
  };

  return {
    onPick,
    prompt,
    pad: () => align('pad'),
    trim: () => align('trim'),
    keep: () => setPrompt(null),
  };
}
